import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Tooltip from '@radix-ui/react-tooltip';
import {
  BarChart3,
  BookOpen,
  Building2,
  ExternalLink,
  FileText,
  FlaskConical,
  Headset,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Mail,
  MessageSquare,
  PanelRightClose,
  PanelRightOpen,
  Settings,
  Shield,
  UserCog,
  Star,
  SwatchBook,
  Target,
  UserCircle,
  Users as UsersIcon,
} from 'lucide-react';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import reachLogo from '../assets/reach-logo-black-text.svg';
import { useAllFlags } from '../hooks/useAllFlags';
import { useAuth } from '../hooks/useAuth';
import { type Partner } from '../lib/api';
import type { LaunchDarklyFlags } from '../types/launch-darkly';
import { appRoutes } from '@/app-routes';
import { OrgSwitcher } from './OrgSwitcher';

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  to?: string;
  requiresFlag?: keyof LaunchDarklyFlags;
}

interface SidebarProps {
  activeItem: string;
  onItemSelect: (itemId: string) => void;
  partner?: Partner | null;
}

interface SidebarSection {
  id: string;
  label: string;
  items: SidebarItem[];
}

const SIDEBAR_SECTIONS: SidebarSection[] = [
  {
    id: 'data',
    label: 'Data',
    items: [
      {
        id: 'metrics',
        label: 'Overview',
        icon: <LayoutDashboard className="h-4 w-4" strokeWidth={1.5} />,
      },
      {
        id: 'tenants',
        label: 'Tenants',
        icon: <Building2 className="h-4 w-4" strokeWidth={1.5} />,
      },
      {
        id: 'users',
        label: 'Users',
        icon: <UsersIcon className="h-4 w-4" strokeWidth={1.5} />,
        to: '/dashboard/users',
      },
      {
        id: 'communications',
        label: 'Communications',
        icon: <MessageSquare className="h-4 w-4" strokeWidth={1.5} />,
        to: '/dashboard/communications',
      },
    ],
  },
  {
    id: 'features',
    label: 'Features',
    items: [
      {
        id: 'acquire',
        label: 'Acquire',
        icon: <Target className="h-4 w-4" strokeWidth={1.5} />,
        to: '/dashboard/acquire',
      },
      {
        id: 'engage',
        label: 'Engage',
        icon: <Mail className="h-4 w-4" strokeWidth={1.5} />,
        to: `${appRoutes.ENGAGE_SETUP}/usage`,
      },
      {
        id: 'measure',
        label: 'Measure',
        icon: <BarChart3 className="h-4 w-4" strokeWidth={1.5} />,
        to: '/dashboard/measure',
      },
      {
        id: 'reputation',
        label: 'Reputation',
        icon: <Star className="h-4 w-4" strokeWidth={1.5} />,
        to: '/dashboard/reputation',
      },
      {
        id: 'voice',
        label: 'Assist',
        icon: <Headset className="h-4 w-4" strokeWidth={1.5} />,
        to: appRoutes.VOICE,
      },
    ],
  },

  {
    id: 'developers',
    label: 'Developers',
    items: [
      {
        id: 'api-keys',
        label: 'API Keys',
        icon: <KeyRound className="h-4 w-4" strokeWidth={1.5} />,
        to: '/dashboard/settings/api',
      },
      {
        id: 'branding',
        label: 'Branding',
        icon: <SwatchBook className="h-4 w-4" strokeWidth={1.5} />,
        to: '/dashboard/settings/theme',
      },
      {
        id: 'data-integration',
        label: 'Data Integration',
        icon: <FileText className="h-4 w-4" strokeWidth={1.5} />,
        to: '/dashboard/settings/data-integration',
      },
      {
        id: 'ai-tools',
        label: 'AI Tools',
        icon: <FlaskConical className="h-4 w-4" strokeWidth={1.5} />,
        to: appRoutes.AI_TOOLS,
        requiresFlag: 'enable-partner-mcp-authoring',
      },
      {
        id: 'ai-credentials',
        label: 'AI Credentials',
        icon: <KeyRound className="h-4 w-4" strokeWidth={1.5} />,
        to: appRoutes.AI_CREDENTIALS,
        requiresFlag: 'enable-partner-mcp-authoring',
      },
      {
        id: 'testing',
        label: 'Testing',
        icon: <FlaskConical className="h-4 w-4" strokeWidth={1.5} />,
      },
      {
        id: 'guides',
        label: 'Guides',
        icon: <FileText className="h-4 w-4" strokeWidth={1.5} />,
      },
      {
        id: 'documentation',
        label: 'API Docs',
        icon: <BookOpen className="h-4 w-4" strokeWidth={1.5} />,
      },
    ],
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ activeItem, onItemSelect, partner }) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const { user, organization, logout } = useAuth();
  const { ld } = useAllFlags();

  const sections = SIDEBAR_SECTIONS.map(section => ({
    ...section,
    items: section.items.filter(item => !item.requiresFlag || ld[item.requiresFlag]),
  }));

  const handleLogout = () => {
    logout();
  };

  return (
    <Tooltip.Provider delayDuration={100}>
      <aside
        className={`bg-slate-800 border-r border-slate-700 transition-all duration-300 ease-in-out ${
          isCollapsed ? 'w-16' : 'w-52'
        }`}>
        <div className="flex flex-col h-full">
          {/* Reach Logo Section */}
          <div className="border-b border-slate-700">
            <div className="flex items-center justify-between px-3 py-3">
              {isCollapsed ? (
                <button
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  className="p-1.5 rounded-md hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                  title="Expand sidebar">
                  <PanelRightClose className="h-4 w-4" />
                </button>
              ) : (
                <>
                  <Link
                    to="/dashboard/metrics"
                    className="flex items-center justify-start transition-colors">
                    <img
                      src={reachLogo}
                      alt="Reach"
                      className="h-4 w-auto brightness-0 invert opacity-80"
                    />
                  </Link>
                  <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="p-1.5 rounded-md hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                    title="Collapse sidebar">
                    <PanelRightOpen className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Partner Info Section with Org Switcher */}
          {!isCollapsed && partner && <OrgSwitcher partner={partner} />}
          {isCollapsed && partner && (
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <div className="px-2 py-4 border-b border-slate-700 flex justify-center">
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center overflow-hidden bg-white">
                    {partner.logoUrl ? (
                      <img
                        src={partner.logoUrl}
                        alt={`${partner.name} logo`}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="h-full w-full bg-whiterounded-lg flex items-center justify-center">
                        <Shield className="h-4 w-4 text-slate-400" />
                      </div>
                    )}
                  </div>
                </div>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="right"
                  sideOffset={8}
                  className="z-50 px-3 py-1.5 text-sm font-medium text-white bg-slate-800 rounded-md shadow-md">
                  {organization?.name || partner.name}
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          )}

          {/* Navigation Items by Section */}
          <nav className="flex-1 px-2 pt-3 pb-2 space-y-3 overflow-y-auto custom-scrollbar hover-scrollbar">
            {sections.map(section => (
              <div key={section.id}>
                {!isCollapsed && (
                  <p className="px-2.5 pb-1.5 text-[10px] uppercase tracking-wide text-slate-500 font-light font-nav">
                    {section.label}
                  </p>
                )}
                <div className="space-y-1">
                  {section.items.map(item => (
                    <Tooltip.Root key={item.id} open={isCollapsed && hoveredItemId === item.id}>
                      <Tooltip.Trigger asChild>
                        <button
                          onClick={() => {
                            if (item.id === 'guides') {
                              window.open(
                                'https://docs.embedreach.com/samples/jwt-tokens',
                                '_blank',
                                'noopener,noreferrer',
                              );
                              return;
                            }
                            if (item.id === 'documentation') {
                              window.open(
                                'https://docs.embedreach.com/api-reference/introduction',
                                '_blank',
                                'noopener,noreferrer',
                              );
                              return;
                            }
                            if (item.id === 'testing') {
                              window.open(
                                'https://docs.embedreach.com/embeddable-ui/iframe/sandbox',
                                '_blank',
                                'noopener,noreferrer',
                              );
                              return;
                            }
                            if (item.to) {
                              navigate(item.to);
                            } else {
                              onItemSelect(item.id);
                            }
                          }}
                          onMouseEnter={() => {
                            if (isCollapsed) setHoveredItemId(item.id);
                          }}
                          onMouseLeave={() => {
                            if (isCollapsed) setHoveredItemId(null);
                          }}
                          className={`w-full flex items-center px-2.5 py-1.5 text-[13px] font-light rounded-md transition-colors ${
                            activeItem === item.id
                              ? 'bg-slate-700 text-white'
                              : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                          } ${isCollapsed ? 'justify-center' : 'justify-start'}`}>
                          {item.icon}
                          {!isCollapsed && (
                            <span className="ml-2.5 font-nav flex items-center gap-1">
                              <span
                                className={
                                  !item.to &&
                                  ![
                                    'metrics',
                                    'tenants',
                                    'communications',
                                    'guides',
                                    'documentation',
                                    'testing',
                                  ].includes(item.id)
                                    ? 'italic'
                                    : ''
                                }>
                                {item.label}
                              </span>
                              {['guides', 'documentation', 'testing'].includes(item.id) && (
                                <ExternalLink className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />
                              )}
                            </span>
                          )}
                        </button>
                      </Tooltip.Trigger>
                      {isCollapsed && (
                        <Tooltip.Portal>
                          <Tooltip.Content
                            side="right"
                            sideOffset={8}
                            className="z-50 px-3 py-1.5 text-sm font-light text-white bg-slate-800 rounded-md shadow-md font-nav">
                            {item.label}
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      )}
                    </Tooltip.Root>
                  ))}
                </div>
              </div>
            ))}

            {/* Settings section */}
            <div className="pt-2">
              {!isCollapsed && (
                <p className="px-2.5 pb-1.5 text-[10px] uppercase tracking-wide text-slate-500 font-light font-nav">
                  Settings
                </p>
              )}
              <div className="space-y-1">
                {[
                  {
                    id: 'profile',
                    label: 'Profile',
                    icon: <Settings className="h-4 w-4" strokeWidth={1.5} />,
                    to: '/dashboard/settings',
                  },
                  {
                    id: 'team',
                    label: 'Team',
                    icon: <UserCog className="h-4 w-4" strokeWidth={1.5} />,
                    to: '/dashboard/settings/team',
                  },
                ].map(item => (
                  <Tooltip.Root key={item.id} open={isCollapsed && hoveredItemId === item.id}>
                    <Tooltip.Trigger asChild>
                      <button
                        onClick={() => navigate(item.to)}
                        onMouseEnter={() => {
                          if (isCollapsed) setHoveredItemId(item.id);
                        }}
                        onMouseLeave={() => {
                          if (isCollapsed) setHoveredItemId(null);
                        }}
                        className={`w-full flex items-center px-2.5 py-1.5 text-[13px] font-light rounded-md transition-colors ${
                          activeItem === item.id
                            ? 'bg-slate-700 text-white'
                            : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                        } ${isCollapsed ? 'justify-center' : 'justify-start'}`}>
                        {item.icon}
                        {!isCollapsed && <span className="ml-2.5 font-nav">{item.label}</span>}
                      </button>
                    </Tooltip.Trigger>
                    {isCollapsed && (
                      <Tooltip.Portal>
                        <Tooltip.Content
                          side="right"
                          sideOffset={8}
                          className="z-50 px-3 py-1.5 text-sm font-light text-white bg-slate-800 rounded-md shadow-md font-nav">
                          {item.label}
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    )}
                  </Tooltip.Root>
                ))}
              </div>
            </div>
          </nav>

          {/* User Info Section */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                className={`px-3 py-3 border-t border-slate-700 w-full hover:bg-slate-700 transition-colors ${isCollapsed ? 'flex justify-center' : ''}`}>
                {isCollapsed ? (
                  <div className="flex items-center justify-center">
                    {user?.profilePictureUrl ? (
                      <img
                        src={user.profilePictureUrl}
                        alt={`${user.firstName} ${user.lastName}`}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <UserCircle className="h-8 w-8 text-slate-400" />
                    )}
                  </div>
                ) : (
                  <div className="flex items-center space-x-3">
                    {user?.profilePictureUrl ? (
                      <img
                        src={user.profilePictureUrl}
                        alt={`${user.firstName} ${user.lastName}`}
                        className="h-8 w-8 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <UserCircle className="h-8 w-8 text-slate-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate font-nav">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                    </div>
                  </div>
                )}
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                side="right"
                align="end"
                sideOffset={8}
                className="z-50 min-w-[200px] bg-white rounded-md shadow-lg border border-slate-200 p-1">
                <DropdownMenu.Item
                  onSelect={e => {
                    e.preventDefault();
                    handleLogout();
                  }}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md cursor-pointer outline-none">
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </aside>
    </Tooltip.Provider>
  );
};
